import React, { useState, useEffect } from 'react';
import { getGroupedPaymentPlans } from '../utils/subscriptionApi';
import { findFamilyPlan } from '../utils/subscriptionPlanCategory';
import { FamilyPlanMarketingCard } from './FamilyPlanMarketingCard';
import { FamilyPlanInquiryModal } from './FamilyPlanInquiryModal';
import { OrganizationDemoCard } from './OrganizationDemoCard';
import '../styles/login-page.css';
import '../styles/landing-page.css';
import '../styles/signup-page.css';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';

const DEFAULT_STUDENTS = 25;

function parseStudents(maxAthletes?: string): number {
  const limit = maxAthletes ? parseInt(maxAthletes, 10) : DEFAULT_STUDENTS;
  return Number.isFinite(limit) && limit > 0 ? limit : DEFAULT_STUDENTS;
}

type PlansTab = 'families' | 'schools';

export function SignupPage() {
  const [familyStudents, setFamilyStudents] = useState(DEFAULT_STUDENTS);
  const [activeTab, setActiveTab] = useState<PlansTab>('families');
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    const fetchFamilyPlan = async () => {
      try {
        const response = await getGroupedPaymentPlans({ familyOnly: true });
        const familyPlan = findFamilyPlan(response.plans);
        if (familyPlan) {
          setFamilyStudents(parseStudents(familyPlan.metadata?.maxAthletes));
        }
      } catch {
        // Keep the default student count; the inquiry CTA does not depend on plan data.
      }
    };

    void fetchFamilyPlan();
  }, []);

  return (
    <div className="signup-page-new">
      <PublicHeader />

      <section className="plans-b" data-testid="signup-plans-hero">
        <div className="plans-b-wrap">
          <div className="plans-b-hero">
            <p className="plans-b-eyebrow" data-testid="signup-plans-label">
              <span className="plans-b-rule" aria-hidden="true" />
              <span className="plans-b-star" aria-hidden="true">
                ✦
              </span>{' '}
              Plans{' '}
              <span className="plans-b-star" aria-hidden="true">
                ✦
              </span>
              <span className="plans-b-rule" aria-hidden="true" />
            </p>
            <h1 className="plans-b-title" data-testid="signup-plans-title">
              Find Your Route
            </h1>
            <p className="plans-b-intro" data-testid="signup-plans-subtitle">
              Built by counselors, coaches, educators, and former student-athletes, Polaris Athlete
              brings together the expertise families often seek across multiple places into one
              connected platform designed specifically for today&apos;s student-athlete journey.
            </p>
          </div>

          <div
            className="plans-b-toggle"
            data-active={activeTab === 'schools' ? '1' : '0'}
            role="tablist"
            aria-label="Choose a plan type"
          >
            <div className="plans-b-toggle-thumb" aria-hidden="true" />
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'families'}
              onClick={() => setActiveTab('families')}
            >
              Families
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'schools'}
              onClick={() => setActiveTab('schools')}
            >
              Schools &amp; Orgs
            </button>
          </div>

          <div className={`plans-b-panel${activeTab === 'families' ? ' active' : ''}`}>
            <FamilyPlanMarketingCard
              students={familyStudents}
              onGetMoreInfo={() => setInquiryOpen(true)}
            />
          </div>

          <div className={`plans-b-panel${activeTab === 'schools' ? ' active' : ''}`}>
            <OrganizationDemoCard />
          </div>
        </div>
      </section>

      <FamilyPlanInquiryModal open={inquiryOpen} onClose={() => setInquiryOpen(false)} />

      <PublicFooter />
    </div>
  );
}

export default SignupPage;
